import React, { useContext } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import { AppContext } from "../../context/app.context";
import CrewHistorySection from "../../components/CrewHistorySection";

/**
 * Time Machine #114 Phase 5 — minimal crew modal for time-travel mode.
 *
 * Per design decision (locked 2026-05-08): when in time-travel, clicking a
 * crew shows ONLY their identity + History feed. The full live CrewDetailModal
 * (capacity grids, availability tab, defaults tab) is overkill for forensic
 * "what changed for this installer" investigation, and re-rendering it with
 * historical data adds complexity for little forensic value. Instead, this
 * minimal modal shows:
 *   - Header: crew name + RecordType + Surface + payrollNo (read-only, just identity)
 *   - <CrewHistorySection>: combined Candidates + Attributes events for this
 *     installer, sorted timestamp-desc, with kind tags ("📅 Availability"
 *     vs "👤 Roster"). Single click expands; double click scrubs the slider.
 *   - Footer: Close button
 *
 * Routed in App.js: when openCrewDetailModal.show && isTimeTraveling, render
 * this in place of the standard CrewDetailModal.
 */
const TimeMachineCrewModal = ({ crewDetail, setOpenCrewDetailModal }) => {
  const { dataFormatting_payrollFields } = useContext(AppContext) || {};

  const close = () => setOpenCrewDetailModal({ show: false });

  if (!crewDetail) {
    return (
      <Modal onClose={close}>
        <Modal.Header>Crew History</Modal.Header>
        <Modal.Content>
          <div className="w-[600px] py-4 text-sm italic text-skin-muted">
            No crew selected.
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button color="light" className="uppercase w-31" onClick={close}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const candidateId = crewDetail._id?.toString?.() ?? crewDetail._id;
  const payrollNoFormatted =
    typeof dataFormatting_payrollFields === "function"
      ? dataFormatting_payrollFields(crewDetail.payrollNo)
      : crewDetail.payrollNo;

  // Phase 6 follow-up (2026-05-09): inner content given a fixed height so
  // the modal sits at max-h-[90%] (Modal.js outer wrapper) from the initial
  // render. Without this, the modal starts SMALL (compact crew identity + a
  // few history rows fits in ~400px) and grows when a History row is
  // expanded — which causes Modal.js's `flex items-center justify-center`
  // outer section to RECENTER the modal vertically on each growth step.
  // The recentering shifts the just-clicked row's TEXT out from under the
  // user's mouse cursor before the second click of a double-click lands.
  // Browsers require both clicks of a dblclick to hit the same DOM target,
  // so the dblclick fails to register ~80% of the time. The user could
  // single-click → expand/collapse but not double-click → scrub.
  //
  // Fix: give the inner column a fixed height (75vh) and let the History
  // section flex-grow to fill the remaining space with its own internal
  // overflow-y. The modal hits its max height from the start; expanding a
  // row scrolls inside the History section without changing the modal's
  // outer dimensions, so the row's text stays put between clicks.
  //
  // JobsDetailModal doesn't need this because its read-only content is
  // already tall enough to hit the modal's max-h-[90%] without any history
  // expansions.
  return (
    <Modal onClose={close}>
      <Modal.Header>Crew: {crewDetail.Name_Full}</Modal.Header>
      <Modal.Content>
        <div
          className="w-[700px] flex flex-col gap-3"
          style={{ height: "75vh" }}
        >
          <div className="grid grid-cols-[100px_auto] gap-y-1 shrink-0">
            <div>Name</div>
            <div className="font-bold">{crewDetail.Name_Full}</div>
            <div>Crew Type</div>
            <div className="font-bold">{crewDetail.RecordType}</div>
            <div>Surface</div>
            <div className="font-bold">{crewDetail.Scheduling_Surface}</div>
            <div>Payroll#</div>
            <div className="font-bold">{payrollNoFormatted}</div>
            {crewDetail.cLocationCode ? (
              <>
                <div>Location</div>
                <div className="font-bold">{crewDetail.cLocationCode}</div>
              </>
            ) : null}
            {Array.isArray(crewDetail.Additional_Locations) &&
            crewDetail.Additional_Locations.length > 0 ? (
              <>
                <div>Also serves</div>
                <div className="font-bold">
                  {crewDetail.Additional_Locations.join(", ")}
                </div>
              </>
            ) : null}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <CrewHistorySection
              candidateId={candidateId}
              payrollNo={payrollNoFormatted}
              onScrubbed={close}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex">
          <Button color="light" className="uppercase w-31" onClick={close}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TimeMachineCrewModal;
